import { Route, Routes, Navigate } from "react-router-dom";
import { AppLayout } from "@/layouts/AppLayout";
import Index from "@/pages/Index";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import Callback from "@/pages/auth/Callback";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import NotFound from "@/pages/NotFound";
import Profile from "@/pages/Profile";
import Courses from "@/pages/Courses";
import CourseDetails from "@/pages/CourseDetails";
import AdminCourses from "@/pages/admin/Courses";
import EditCourse from "@/pages/admin/EditCourse";
import CreateCourse from "@/pages/admin/CreateCourse";
import AttendanceVerification from "@/pages/admin/AttendanceVerification";
import CheckIn from "@/pages/CheckIn";
import Dashboard from "@/pages/Dashboard";
import Certificates from "@/pages/Certificates";
import Documentation from "@/pages/Documentation";
import ManageCertificates from "@/pages/admin/ManageCertificates";
import ManageUsers from "@/pages/admin/ManageUsers";
import UserRoleChecker from "@/pages/admin/UserRoleChecker";
import Analytics from "@/pages/Analytics";
import CategoryAnalytics from "@/pages/CategoryAnalytics";
import { StaffRouteGuard } from "@/components/guards/StaffRouteGuard";
import { TechRouteGuard } from "@/components/guards/TechRouteGuard";
import { AuthGuard } from "@/components/guards/AuthGuard";
import { PublicOnlyGuard } from "@/components/guards/PublicOnlyGuard";
import FeedbackReports from "@/pages/admin/FeedbackReports";
import FeedbackAnalytics from "@/pages/admin/FeedbackAnalytics";
import CourseFeedbackDetails from "@/pages/admin/CourseFeedbackDetails";
import Schedule from "@/pages/Schedule";
import BosaRequests from "@/pages/admin/BosaRequests";
import BosaRequest from "@/pages/BosaRequest";
import BosaRequestStatus from "@/pages/BosaRequestStatus";
import FeatureRequests from "@/pages/tech/FeatureRequests";
import SystemMonitor from "@/pages/tech/SystemMonitor";
import TechnicalLogs from "@/pages/tech/TechnicalLogs";
import FeatureImplementation from "@/pages/tech/FeatureImplementation";
import SupportHome from "@/pages/support/SupportHome";
import TicketsList from "@/pages/support/TicketsList";
import NewTicket from "@/pages/support/NewTicket";
import TicketDetail from "@/pages/support/TicketDetail";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import PublicEvents from "@/pages/PublicEvents";

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes (accessible without authentication) */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/events" element={<PublicEvents />} />
      <Route path="/index" element={<Index />} /> {/* Keep Index page at /index */}
      
      {/* Redirect root to dashboard for authenticated users */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* Public Only Routes (accessible only when NOT logged in) */}
      <Route element={<PublicOnlyGuard />}>
        <Route path="/auth/login" element={<Login />} />
        <Route path="/auth/register" element={<Register />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<Callback />} />
      </Route>

      {/* Protected Routes (require authentication) */}
      <Route element={<AppLayout />}>
        <Route element={<AuthGuard />}>
          {/* Dashboard route for authenticated users */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:id" element={<CourseDetails />} />
          <Route path="/certificates" element={<Certificates />} />
          <Route path="/documentation" element={<Documentation />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/bosa-request" element={<BosaRequest />} />
          <Route path="/bosa-requests" element={<BosaRequestStatus />} />
          
          {/* Support Center Routes */}
          <Route path="/support" element={<SupportHome />} />
          <Route path="/support/tickets" element={<TicketsList />} />
          <Route path="/support/tickets/new" element={<NewTicket />} />
          <Route path="/support/tickets/:id" element={<TicketDetail />} />
          
          <Route 
            path="/analytics/categories" 
            element={
              <StaffRouteGuard>
                <CategoryAnalytics />
              </StaffRouteGuard>
            } 
          />
          
          {/* Changed from StaffRouteGuard to TechRouteGuard */}
          <Route 
            path="/check-in" 
            element={
              <TechRouteGuard>
                <CheckIn />
              </TechRouteGuard>
            } 
          />
          <Route 
            path="/admin/courses" 
            element={
              <StaffRouteGuard>
                <AdminCourses />
              </StaffRouteGuard>
            } 
          />
          <Route 
            path="/admin/courses/create" 
            element={
              <StaffRouteGuard>
                <CreateCourse />
              </StaffRouteGuard>
            } 
          />
          <Route 
            path="/admin/courses/:id" 
            element={
              <StaffRouteGuard>
                <EditCourse />
              </StaffRouteGuard>
            } 
          />
          <Route 
            path="/admin/courses/:courseId/attendance" 
            element={
              <StaffRouteGuard>
                <AttendanceVerification />
              </StaffRouteGuard>
            } 
          />
          <Route 
            path="/admin/certificates" 
            element={
              <StaffRouteGuard>
                <ManageCertificates />
              </StaffRouteGuard>
            } 
          />
          <Route 
            path="/admin/bosa-requests" 
            element={
              <StaffRouteGuard>
                <BosaRequests />
              </StaffRouteGuard>
            } 
          />
          {/* Changed from StaffRouteGuard to TechRouteGuard */}
          <Route 
            path="/admin/users" 
            element={
              <TechRouteGuard>
                <ManageUsers />
              </TechRouteGuard>
            } 
          />
          <Route 
            path="/admin/user-role-checker" 
            element={
              <TechRouteGuard>
                <UserRoleChecker />
              </TechRouteGuard>
            } 
          />
          <Route 
            path="/admin/feedback" 
            element={
              <StaffRouteGuard>
                <FeedbackReports />
              </StaffRouteGuard>
            } 
          />
          <Route 
            path="/admin/feedback/analytics" 
            element={
              <StaffRouteGuard>
                <FeedbackAnalytics />
              </StaffRouteGuard>
            } 
          />
          <Route 
            path="/admin/feedback/course/:courseId" 
            element={
              <StaffRouteGuard>
                <CourseFeedbackDetails />
              </StaffRouteGuard>
            } 
          />
          
          {/* Tech User Routes */}
          <Route 
            path="/tech/feature-requests" 
            element={
              <TechRouteGuard>
                <FeatureRequests />
              </TechRouteGuard>
            } 
          />
          <Route 
            path="/tech/system-monitor" 
            element={
              <TechRouteGuard>
                <SystemMonitor />
              </TechRouteGuard>
            } 
          />
          <Route 
            path="/tech/technical-logs" 
            element={
              <TechRouteGuard>
                <TechnicalLogs />
              </TechRouteGuard>
            } 
          />
          <Route 
            path="/tech/feature-implementation" 
            element={
              <TechRouteGuard>
                <FeatureImplementation />
              </TechRouteGuard>
            } 
          />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
